import { useEffect, useState, useCallback } from "react";
import { Truck, PackagePlus, Loader2, AlertTriangle, Building2, GitCompare, Plus, Trash2, Award } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const money = (n) => `$${(Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Portal de proveedores: refacciones por reabastecer, recepción de compras que
// reingresa al inventario, y RFQ (cotización comparativa entre proveedores).
export default function WorkshopSupplierPortal() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recv, setRecv] = useState({});   // purchaseId -> {numero_parte, cantidad}
  const [busy, setBusy] = useState("");
  const [rfqs, setRfqs] = useState([]);
  const [rfq, setRfq] = useState({ descripcion: "", quotes: [{ proveedor: "", precio: "" }, { proveedor: "", precio: "" }] });

  const load = useCallback(() => {
    setLoading(true);
    api.get("/workshop/supplier-portal").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/workshop/rfq").then((r) => setRfqs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const createRfq = async () => {
    const quotes = rfq.quotes.filter((q) => q.proveedor.trim() && Number(q.precio) > 0);
    if (!rfq.descripcion.trim() || quotes.length < 2) { toast.error("Indica descripción y al menos 2 proveedores con precio"); return; }
    setBusy("rfq");
    try {
      await api.post("/workshop/rfq", { descripcion: rfq.descripcion.trim(), cotizaciones: quotes.map((q) => ({ proveedor: q.proveedor.trim(), precio: Number(q.precio) })) });
      toast.success("RFQ creada · mejor precio identificado");
      setRfq({ descripcion: "", quotes: [{ proveedor: "", precio: "" }, { proveedor: "", precio: "" }] });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(""); }
  };
  const setQuote = (i, k, v) => setRfq((s) => ({ ...s, quotes: s.quotes.map((q, idx) => idx === i ? { ...q, [k]: v } : q) }));

  const receive = async (pid) => {
    const r = recv[pid] || {};
    if (!r.numero_parte || !(Number(r.cantidad) > 0)) { toast.error("Captura No. de parte y cantidad"); return; }
    setBusy(pid);
    try {
      const { data: res } = await api.post(`/workshop/purchases/${pid}/receive`, { lineas: [{ numero_parte: r.numero_parte.trim(), cantidad: Number(r.cantidad) }] });
      toast.success(`Recibidas ${res.restocked} pzs · inventario reabastecido`);
      setRecv((s) => ({ ...s, [pid]: {} }));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(""); }
  };

  const setField = (pid, k, v) => setRecv((s) => ({ ...s, [pid]: { ...(s[pid] || {}), [k]: v } }));
  const r = data?.resumen || {};

  return (
    <div data-testid="workshop-supplier-portal">
      <div className="grid grid-cols-3 gap-3 mb-5 max-w-md">
        {[["Por reabastecer", r.por_reabastecer || 0, true], ["Proveedores", r.proveedores || 0], ["Órdenes abiertas", r.ordenes_abiertas || 0]].map(([l, v, warn]) => (
          <div key={l} className="card-surface p-3"><div className="text-[11px] text-navy/40">{l}</div><div className={`text-lg font-medium ${warn && v > 0 ? "text-amber-600" : ""}`}>{v}</div></div>
        ))}
      </div>

      {loading ? <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Bajo stock */}
          <div>
            <div className="flex items-center gap-2 overline text-navy/40 mb-2"><AlertTriangle className="w-3.5 h-3.5" /> Refacciones por reabastecer</div>
            {!data?.low_stock?.length ? (
              <div className="card-surface p-6 text-center text-sm text-navy/40">Inventario en niveles saludables.</div>
            ) : (
              <div className="card-surface divide-y divide-navy/5">
                {data.low_stock.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`low-stock-${p.numero_parte}`}>
                    <div className="min-w-0 flex-1"><div className="text-sm truncate">{p.descripcion}</div><div className="text-[11px] text-navy/40 flex items-center gap-1"><Building2 className="w-3 h-3" />{p.proveedor} · {p.numero_parte}</div></div>
                    <div className="text-right text-xs"><div className="text-amber-600 font-medium">{p.stock}/{p.minimo}</div><div className="text-navy/40">sugerido {p.sugerido}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Órdenes de compra */}
          <div>
            <div className="flex items-center gap-2 overline text-navy/40 mb-2"><Truck className="w-3.5 h-3.5" /> Órdenes de compra</div>
            {!data?.purchases?.length ? (
              <div className="card-surface p-6 text-center text-sm text-navy/40">Sin órdenes de compra. Créalas en <b>Compras</b>.</div>
            ) : (
              <div className="space-y-2">
                {data.purchases.map((po) => (
                  <div key={po.id} className="card-surface p-3" data-testid={`purchase-${po.folio || po.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-sm font-medium">{po.folio || "Compra"} · {po.proveedor}</div>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${po.estado === "recibido" ? "bg-emerald-100 text-emerald-700" : "bg-navy/5 text-navy/50"}`}>{po.estado === "recibido" ? "Recibida" : (po.estado || "abierta")}</span>
                    </div>
                    {po.estado !== "recibido" && (
                      <div className="flex gap-2 mt-2">
                        <input value={(recv[po.id] || {}).numero_parte || ""} onChange={(e) => setField(po.id, "numero_parte", e.target.value)} placeholder="No. parte" className="field-sm flex-1" data-testid={`recv-part-${po.folio || po.id}`} />
                        <input type="number" min="0" value={(recv[po.id] || {}).cantidad || ""} onChange={(e) => setField(po.id, "cantidad", e.target.value)} placeholder="Cant." className="w-20 field-sm" data-testid={`recv-qty-${po.folio || po.id}`} />
                        <button onClick={() => receive(po.id)} disabled={busy === po.id} className="btn btn-primary text-sm" data-testid={`recv-btn-${po.folio || po.id}`}>
                          {busy === po.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackagePlus className="w-4 h-4" />} Recibir
                        </button>
                      </div>
                    )}
                    {(po.recepciones || []).length > 0 && <div className="text-[11px] text-navy/40 mt-1.5">{po.recepciones.length} recepción(es) · última {po.recepciones.at(-1).piezas} pzs</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* RFQ — cotización comparativa */}
      <div className="mt-6" data-testid="rfq-section">
        <div className="flex items-center gap-2 overline text-navy/40 mb-2"><GitCompare className="w-3.5 h-3.5" /> Cotización comparativa (RFQ)</div>
        <div className="card-surface p-4 mb-3">
          <input value={rfq.descripcion} onChange={(e) => setRfq({ ...rfq, descripcion: e.target.value })} placeholder="Refacción / servicio a cotizar" className="field-sm w-full mb-2" data-testid="rfq-desc" />
          {rfq.quotes.map((q, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input value={q.proveedor} onChange={(e) => setQuote(i, "proveedor", e.target.value)} placeholder={`Proveedor ${i + 1}`} className="field-sm flex-1" data-testid={`rfq-prov-${i}`} />
              <input type="number" min="0" value={q.precio} onChange={(e) => setQuote(i, "precio", e.target.value)} placeholder="Precio" className="w-28 field-sm" data-testid={`rfq-price-${i}`} />
            </div>
          ))}
          <div className="flex gap-2">
            <button onClick={() => setRfq((s) => ({ ...s, quotes: [...s.quotes, { proveedor: "", precio: "" }] }))} className="btn btn-ghost text-sm"><Plus className="w-3.5 h-3.5" /> Proveedor</button>
            <button onClick={createRfq} disabled={busy === "rfq"} className="btn btn-primary text-sm ml-auto" data-testid="rfq-create">
              {busy === "rfq" ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompare className="w-4 h-4" />} Comparar y guardar
            </button>
          </div>
        </div>
        {rfqs.length > 0 && (
          <div className="space-y-2">
            {rfqs.map((q) => (
              <div key={q.id} className="card-surface p-3" data-testid={`rfq-row-${q.id}`}>
                <div className="text-sm font-medium mb-1">{q.descripcion}</div>
                <div className="flex flex-wrap gap-2">
                  {(q.cotizaciones || []).map((c, i) => (
                    <span key={i} className={`text-xs px-2.5 py-1 rounded-full flex items-center gap-1 ${c.proveedor === q.mejor_proveedor ? "bg-emerald-100 text-emerald-700 font-medium" : "bg-navy/5 text-navy/60"}`}>
                      {c.proveedor === q.mejor_proveedor && <Award className="w-3 h-3" />}{c.proveedor}: {money(c.precio)}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
